import { useEffect, useState } from "react";
import clsx from "clsx";
import { API_BASE } from "@/lib/apiClient";
import { initialsFromName } from "@/pages/kontakte/geschaeftspartnerColumns";

function geschaeftspartnerLogoSrc(
  partnerId: number,
  updatedAt?: string | null,
): string {
  const stamp = updatedAt ? `?v=${encodeURIComponent(updatedAt)}` : "";
  const path = `/kontakte/geschaeftspartner/${partnerId}/logo${stamp}`;
  return API_BASE ? `${API_BASE}${path}` : path;
}

/**
 * Firmenlogo oder Initialen. Das Bild kommt vom authentifizierten Logo-Endpunkt;
 * ohne Logo oder bei Ladefehler bleiben die Initialen stehen.
 */
export function GeschaeftspartnerLogo({
  partnerId,
  name,
  hasLogo,
  logoUpdatedAt,
  className,
  variant = "accent",
  title,
}: {
  partnerId?: number | null;
  name: string;
  hasLogo?: boolean | null;
  logoUpdatedAt?: string | null;
  className?: string;
  /** `plain` bleibt auf farbigen Zeilen lesbar, z. B. in Auswahllisten. */
  variant?: "accent" | "plain";
  title?: string;
}) {
  const [failed, setFailed] = useState(false);
  const id = Number(partnerId);
  const showImage = Boolean(hasLogo) && Number.isFinite(id) && id > 0 && !failed;

  useEffect(() => {
    setFailed(false);
  }, [id, logoUpdatedAt, hasLogo]);

  return (
    <span
      className={clsx(
        "inline-flex shrink-0 items-center justify-center overflow-hidden font-semibold tracking-wide",
        variant === "plain"
          ? "bg-white text-[#1a1a1a] ring-1 ring-inset ring-black/15"
          : "bg-accent text-label-on-accent",
        className,
      )}
      title={title}
      aria-hidden
    >
      {showImage ? (
        <img
          src={geschaeftspartnerLogoSrc(id, logoUpdatedAt)}
          alt=""
          className="h-full w-full bg-white object-contain"
          onError={() => setFailed(true)}
        />
      ) : (
        initialsFromName(name)
      )}
    </span>
  );
}

/** Logo des Lieferanten, sonst Initialen. Ohne Namen wird nichts gezeigt. */
export function SupplierLogo({
  name,
  partnerId,
  hasLogo,
  logoUpdatedAt,
  className = "h-9 w-9 rounded-lg text-[11px]",
  variant = "accent",
}: {
  name?: string | null;
  partnerId?: number | null;
  hasLogo?: boolean | null;
  logoUpdatedAt?: string | null;
  className?: string;
  variant?: "accent" | "plain";
}) {
  const label = (name || "").trim();
  if (!label || label === "—") return null;
  return (
    <GeschaeftspartnerLogo
      partnerId={partnerId}
      name={label}
      hasLogo={hasLogo}
      logoUpdatedAt={logoUpdatedAt}
      variant={variant}
      title={label}
      className={className}
    />
  );
}
